/*
 * <usp-strip-carousel> — progressive enhancement for the USP strip.
 *
 * The markup is a plain grid that CSS turns into a horizontal scroll-snap
 * track at ≤989px (mobile + tablet). This component only wires up the dot
 * indicators: clicking a dot scrolls to that slide, and the active dot follows
 * the slide that is currently centred in the track. On desktop the dots are
 * hidden via CSS, so the work here is harmless even though it still runs.
 */
class UspStripCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-usp-track]');
    this.dots = Array.from(this.querySelectorAll('[data-usp-dot]'));
    this.slides = this.track
      ? Array.from(this.track.querySelectorAll('.usp-strip__item'))
      : [];

    // Nothing to enhance without a track and at least two slides + dots.
    if (!this.track || this.dots.length < 2 || this.slides.length < 2) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToSlide(index));
    });

    // IntersectionObserver (rooted on the scroll track) tells us which slide is
    // centred without listening to noisy scroll events. 0.6 threshold = the
    // slide taking up most of the viewport wins.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = this.slides.indexOf(entry.target);
          if (index !== -1) this.setActiveDot(index);
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
    this.setActiveDot(0);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  scrollToSlide(index) {
    const slide = this.slides[index];
    if (!slide) return;

    // Robust against the track not being the slide's offsetParent: scroll by
    // the delta between the slide's edge and the track's edge.
    const delta =
      slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this.track.scrollBy({
      left: delta,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  setActiveDot(index) {
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      // aria-current marks the dot for the slide currently in view.
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('usp-strip-carousel')) {
  customElements.define('usp-strip-carousel', UspStripCarousel);
}
